/**
 * The cycle view — SPEC §9.3, Job 1: "who still owes me availability?"
 *
 * Tiles by status, each opening the exact list behind it. The tile and the list come from
 * one server call precisely so they cannot disagree — a tile reading "6 no response" that
 * opens a list of five is worse than no tile.
 *
 * ## On leave is an answer, not silence
 *
 * Somebody who told us they are away has responded, and never appears in a chase list
 * (SPEC §3.7). Counting them as outstanding is how a tool teaches you to ignore its counts.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate, parseISO } from '@gearup/shared';
import {
  ApiError,
  adminApi,
  type CycleSummary,
  type InstructorRow,
  type MonthStatus,
  type ReminderResult,
} from '../../lib/api.js';

const TILES: { status: MonthStatus; label: string; hint: string }[] = [
  { status: 'NO_RESPONSE', label: 'No response', hint: 'Nothing from them yet' },
  { status: 'PARTIAL', label: 'Started', hint: 'Saved but never sent' },
  { status: 'PENDING_REMAINDER', label: 'Part-confirmed', hint: 'Sure only up to a date' },
  { status: 'COMPLETE', label: 'Confirmed', hint: 'Done' },
  { status: 'ON_LEAVE', label: 'On leave', hint: 'Told us they are away' },
];

/**
 * Never chased (SPEC §6). The server enforces this too, and re-derives status when the
 * request lands — this only keeps the button honest about what it is about to do.
 */
const NEVER_CHASE: MonthStatus[] = ['COMPLETE', 'ON_LEAVE'];

/** Said in words, because "skipped" alone reads as a failure. */
const SKIP_REASONS: Record<string, string> = {
  already_complete: 'already confirmed',
  on_leave: 'on leave',
  inactive: 'no longer active',
  fixture: 'test account',
  send_failed: 'send failed',
  already_sent: 'already reminded this month',
};

const STATUS_CLASS: Record<MonthStatus, string> = {
  NO_RESPONSE: 'none',
  PARTIAL: 'partial',
  PENDING_REMAINDER: 'partial',
  COMPLETE: 'complete',
  ON_LEAVE: 'none',
};

export function CycleView({ cycleKey }: { cycleKey: string }) {
  const [summary, setSummary] = useState<CycleSummary | null>(null);
  const [rows, setRows] = useState<InstructorRow[]>([]);
  const [filter, setFilter] = useState<MonthStatus | 'ALL'>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastReminded, setLastReminded] = useState<Record<string, { at: string }>>({});
  const [quota, setQuota] = useState<{ sentToday: number; dailyCap: number } | null>(null);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<ReminderResult | null>(null);

  const loadReminderStatus = useCallback(async () => {
    const status = await adminApi.remindersStatus(cycleKey);
    setLastReminded(status.lastReminded);
    setQuota({ sentToday: status.sentToday, dailyCap: status.dailyCap });
  }, [cycleKey]);

  useEffect(() => {
    void loadReminderStatus().catch(() => undefined);
  }, [loadReminderStatus]);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    setResult(null);
    setConfirming(false);
    void adminApi
      .cycle(cycleKey)
      .then((data) => {
        if (canceled) return;
        setSummary(data.summary);
        setRows(data.instructors);
      })
      .catch(() => {
        if (!canceled) setError('Could not load the roster.');
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [cycleKey]);

  const shown = useMemo(
    () => (filter === 'ALL' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const patternless = useMemo(() => rows.filter((r) => !r.hasPattern).length, [rows]);

  /**
   * Exactly who the button would email: the filtered set, minus anyone who must never be
   * chased. "Remind all" acting on an ambiguous "everyone pending" is what SPEC §9.3 rules
   * out, so the button names its own set and the confirm step lists them.
   */
  const chaseable = useMemo(
    () => shown.filter((r) => !NEVER_CHASE.includes(r.status)),
    [shown],
  );

  async function send() {
    setSending(true);
    setError(null);
    try {
      const outcome = await adminApi.sendReminders(
        cycleKey,
        chaseable.map((r) => r.instructorId),
      );
      setResult(outcome);
      setConfirming(false);
      setQuota({ sentToday: outcome.sentToday, dailyCap: outcome.dailyCap });
      await loadReminderStatus().catch(() => undefined);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send those reminders.');
    } finally {
      setSending(false);
    }
  }

  if (loading && !summary) return <p className="muted center">Loading…</p>;
  if (error) return <p className="notice notice--error">{error}</p>;
  if (!summary) return null;

  const deadlineText = summary.pastDeadline
    ? `Deadline passed ${Math.abs(summary.daysUntilDeadline)} day${Math.abs(summary.daysUntilDeadline) === 1 ? '' : 's'} ago`
    : `${summary.daysUntilDeadline} day${summary.daysUntilDeadline === 1 ? '' : 's'} until the deadline`;

  return (
    <div className="stack">
      <div className="card stack">
        <div className="row row--between row--wrap">
          <h1 style={{ margin: 0 }}>{summary.label} collection</h1>
          <span className={`badge badge--${summary.pastDeadline ? 'partial' : 'complete'}`}>
            {deadlineText}
          </span>
        </div>

        <div className="progress" role="img" aria-label={`${summary.complete} of ${summary.total} confirmed`}>
          <div
            className="progress__bar"
            style={{ width: `${summary.total ? (summary.complete / summary.total) * 100 : 0}%` }}
          />
        </div>
        <p className="muted" style={{ margin: 0 }}>
          <strong>{summary.complete} of {summary.total}</strong> confirmed ·{' '}
          {summary.outstanding} still outstanding
        </p>

        {patternless > 0 && (
          <p className="small muted" style={{ margin: 0 }}>
            {patternless} instructor{patternless === 1 ? ' has' : 's have'} no usual schedule set,
            so every month starts blank for them.
          </p>
        )}
      </div>

      <div className="tiles">
        <button
          type="button"
          className={`tile${filter === 'ALL' ? ' tile--on' : ''}`}
          aria-pressed={filter === 'ALL'}
          onClick={() => setFilter('ALL')}
        >
          <span className="tile__count">{summary.total}</span>
          <span className="tile__label">Everyone</span>
        </button>
        {TILES.map((t) => (
          <button
            key={t.status}
            type="button"
            className={`tile${filter === t.status ? ' tile--on' : ''}`}
            aria-pressed={filter === t.status}
            onClick={() => setFilter(t.status)}
            title={t.hint}
          >
            <span className="tile__count">{summary.counts[t.status]}</span>
            <span className="tile__label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="card stack">
        <div className="row row--between row--wrap">
          <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>
            {filter === 'ALL' ? 'Everyone' : TILES.find((t) => t.status === filter)?.label}
            {': '}
            {shown.length}
          </h2>

          {chaseable.length > 0 && !confirming && !result && (
            <button className="btn btn--secondary" disabled={sending} onClick={() => setConfirming(true)}>
              Remind {chaseable.length}
            </button>
          )}
        </div>

        {/* Names the exact set before anything is sent. SPEC §9.3 rules out a bulk send on an
            ambiguous "everyone pending", and a count alone is still ambiguous. */}
        {confirming && (
          <div className="notice notice--info" style={{ alignItems: 'flex-start' }}>
            <span className="notice__icon" aria-hidden="true">?</span>
            <span className="grow">
              Email <strong>{chaseable.length} instructor{chaseable.length === 1 ? '' : 's'}</strong>{' '}
              about {summary.label}?
              <br />
              <span className="small">{chaseable.map((r) => r.name).join(', ')}</span>
              {shown.length > chaseable.length && (
                <>
                  <br />
                  <span className="small muted">
                    {shown.length - chaseable.length} in this list will be skipped: already
                    confirmed, or on leave.
                  </span>
                </>
              )}
              {quota && quota.sentToday + chaseable.length > quota.dailyCap * 0.8 && (
                <>
                  <br />
                  <span className="small">
                    {quota.sentToday} of {quota.dailyCap} emails used in the last 24 hours.
                    Sign-in links share this allowance.
                  </span>
                </>
              )}
              <br />
              <span className="row" style={{ marginTop: 8 }}>
                <button className="btn btn--primary" disabled={sending} onClick={send}>
                  {sending ? 'Sending…' : `Send ${chaseable.length}`}
                </button>
                <button className="btn btn--secondary" disabled={sending} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </span>
            </span>
          </div>
        )}

        {result && (
          <div
            className={`notice notice--${result.sent > 0 ? 'success' : 'warn'}`}
            role="status"
            style={{ alignItems: 'flex-start' }}
          >
            <span className="notice__icon" aria-hidden="true">{result.sent > 0 ? '✓' : '!'}</span>
            <span className="grow">
              Sent <strong>{result.sent}</strong> of {result.requested}.
              {/* Every skip is named. A silent skip is indistinguishable from a silent
                  failure, and the two need very different responses. */}
              {result.outcomes.some((o) => !o.sent) && (
                <>
                  <br />
                  <span className="small">
                    {result.outcomes
                      .filter((o) => !o.sent)
                      .map((o) => `${o.name} (${SKIP_REASONS[o.skipped ?? 'send_failed']})`)
                      .join(', ')}
                  </span>
                </>
              )}
              <br />
              <button
                className="btn btn--ghost"
                style={{ padding: '4px 0', minHeight: 32 }}
                onClick={() => setResult(null)}
              >
                Dismiss
              </button>
            </span>
          </div>
        )}

        {shown.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nobody in this group.</p>
        ) : (
          <ul className="daylist">
            {shown.map((r) => (
              <li key={r.instructorId} className="daylist__row">
                <span className="daylist__date">
                  {r.name}
                  <br />
                  <span className="daylist__meta">
                    {/* Both numbers, always — "5 confirmed, 4 unconfirmed" says a phone call
                        might close the gap, which neither says alone (SPEC §3.3.3). */}
                    {/* Classes, and days when they differ — "11 confirmed" alone does not
                        say whether that is eleven days or seven days holding eleven
                        classes, and those are very different for planning. */}
                    {r.confirmed} confirmed
                    {r.days > 0 && r.confirmed !== r.days && ` across ${r.days} day${r.days === 1 ? '' : 's'}`}
                    {r.unconfirmed > 0 && ` · ${r.unconfirmed} not yet confirmed`}
                    {/* Excluded from the count above, and said out loud. Silently dropping
                        them would read as a submission going missing. */}
                    {r.awayDays > 0 && ` · ${r.awayDays} inside a break`}
                    {r.confirmedThrough && ` · sure through ${formatDate(parseISO(r.confirmedThrough))}`}
                    {!r.hasPattern && ' · no usual schedule'}
                    {r.locations.length > 0 && ` · ${r.locations.join(', ')}`}
                    {lastReminded[r.instructorId] && (
                      <> · reminded {new Date(lastReminded[r.instructorId]!.at).toLocaleDateString()}</>
                    )}
                  </span>
                  {/* A PARTIAL break leaves the status NO_RESPONSE, correctly — they can
                      still tell us about the rest of the month. But chasing somebody without
                      knowing means asking about days they have already told us they are
                      away, so the row says it even though the tile cannot. */}
                  {r.leaves.filter((l) => !l.wholeMonth).length > 0 && (
                    <>
                      <br />
                      <span className="daylist__meta">
                        {/* Every break touching this month. One person can be away twice in
                            it, and naming only the first is how the second gets chased. */}
                        Away{' '}
                        {r.leaves
                          .filter((l) => !l.wholeMonth)
                          .map(
                            (l) =>
                              `${formatDate(parseISO(l.startDate))} to ${formatDate(parseISO(l.endDate))}`,
                          )
                          .join(', and ')}
                      </span>
                    </>
                  )}
                  {r.note && (
                    <>
                      <br />
                      <span className="daylist__meta">“{r.note}”</span>
                    </>
                  )}
                </span>
                <span className={`badge badge--${STATUS_CLASS[r.status]}`}>
                  {TILES.find((t) => t.status === r.status)?.label ?? r.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
